/* eslint-disable */
import Tooltip from "@/lib/components/ui/Tooltip/Tooltip";
import { cva } from "class-variance-authority";
import {
  ButtonHTMLAttributes,
  MouseEvent,
  Ref,
  RefAttributes,
  forwardRef,
} from "react";

import { cn } from "@/lib/utils";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Icon } from "@/lib/components/ui/Icon/Icon";
import { ButtonType } from "@/lib/types/Button";

const ButtonVariants = cva(
  "inline-flex items-center justify-center text-center font-normal transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none",
  {
    variants: {
      variant: {
        primary:
          "text-[var(--white,#fff)] !bg-[var(--blue-blue-4,var(--blue-4,#0042EB))] hover:bg-[var(--blue-blue-3,#0042EB)] active:bg-[var(--blue-blue-2,#2697FF)] disabled:!bg-[var(--grey-grey-2,#CBCBCB)] disabled:text-[var(--grey-grey-4,#403F3F)]",
        secondary:
          "bg-transparent border-2 border-[var(--blue-blue-3,#0042EB)] text-[var(--blue-blue-3,#0042EB)] hover:bg-[var(--blue-blue-1,#E1E9FF)] active:border-[var(--grey-grey-4,#403F3F)] active:text-[var(--grey-grey-4,#403F3F)] disabled:border-[var(--grey-grey-2,#CBCBCB)] disabled:text-[var(--grey-grey-3,#959595)] disabled:bg-transparent",
        inverted:
          "border-2 border-[var(--grey-grey-2,#CBCBCB)] text-[var(--white,#ffffff)] hover:bg-[var(--white,#FFF)] hover:text-[var(--grey-grey-4,#333333)] hover:border-[var(--white,#ffffff)] hover:shadow-[0_5px_10px_0_rgba(0,0,0,0.10),0_10px_30px_0_rgba(0,0,0,0.15)] disabled:border-[var(--grey-grey-3,#959595)] disabled:text-[var(--grey-grey-2,#CBCBCB)] disabled:bg-transparent",
        tertiary:
          "bg-transparent border-2 border-[var(--blue-blue-3,#0042EB)] text-[var(--blue-blue-3,#0042EB)] hover:bg-[var(--blue-blue-1,#E1E9FF)] active:border-[var(--grey-grey-4,#403F3F)] active:text-[var(--grey-grey-4,#403F3F)] disabled:border-[var(--grey-grey-2,#CBCBCB)] disabled:text-[var(--grey-grey-3,#959595)] disabled:bg-transparent",
        danger:
          "border border-red-500 bg-red-500 hover:bg-red-400 text-white disabled:border-[var(--grey-grey-2,#CBCBCB)] disabled:bg-[var(--grey-grey-2,#CBCBCB)] disabled:text-[var(--grey-grey-4,#403F3F)]",
        invertedDanger:
          "bg-transparent text-[var(--Colors-Red-Red2,#EB583C)] rounded-[var(--Corner-radius-S,8px)] border-2 border-[var(--Colors-Red-Red2,#EB583C)] disabled:border-[var(--grey-grey-2,#CBCBCB)] disabled:text-[var(--grey-grey-3,#959595)]",
        text: "bg-transparent p-0 border-none text-inherit hover:opacity-80 disabled:text-[var(--grey-grey-3,#959595)]",
        textOutlined:
          "bg-transparent p-0 text-inherit hover:opacity-80 rounded-[var(--Corner-radius-S,8px)] border-2 border-[--grey-4] disabled:border-[var(--grey-grey-2,#CBCBCB)] disabled:text-[var(--grey-grey-3,#959595)]",
        invertedSuccess:
          "bg-transparent text-[var(--green-4)] rounded-[var(--Corner-radius-S,8px)] border-2 border-[var(--green-2)] disabled:border-[var(--grey-2)] disabled:text-[var(--grey-3)]",
      },
      size: {
        l: "h-12 min-w-[160px] max-w-[350px] px-5 gap-4 text-base rounded-[var(--br-s,8px)] max-[720px]:h-[42px] max-[720px]:gap-2 max-[720px]:px-3 max-[720px]:max-w-full max-[720px]:min-w-[120px]",
        m: "h-[47px] px-4 gap-2 text-base rounded-[var(--br-s,8px)]",
        s: "h-8 px-4 py-2 gap-2 text-xs whitespace-nowrap shrink-0 rounded-[var(--br-s,8px)]",
      },
      invertedTone: {
        default: "",
        black:
          "border-[var(--black,#000)] text-[var(--black,#000)] hover:bg-[var(--black,#000)] hover:text-[var(--white,#ffffff)]",
        grey: "border-[var(--grey-grey-3,#959595)] text-[var(--grey-grey-3,#959595)]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "m",
      invertedTone: "default",
    },
  },
);
export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "onClick">,
    ButtonType {}

const Button = forwardRef(
  (
    {
      className,
      children,
      variant,
      size,
      invertedTone,
      label,
      iconName,
      hidden,
      isLoading,
      tooltip,
      disabledTooltip,
      iconStart,
      iconEnd,
      fullWidth,
      onClick,
      ...props
    }: ButtonProps,
    forwardedRef,
  ): JSX.Element => {
    const isDisabled = Boolean(props.disabled) || Boolean(isLoading);
    const resolvedTooltip =
      tooltip ?? (isDisabled ? disabledTooltip : undefined);
    const resolvedSize = size ?? "m";
    const resolvedIconStart =
      iconStart ??
      (iconName && iconEnd !== true ? (
        <Icon
          name={iconName}
          size={resolvedSize === "s" ? "small" : "normal"}
          color="inherit"
        />
      ) : null);
    const resolvedIconEnd =
      iconEnd === true && iconName ? (
        <Icon
          name={iconName}
          size={resolvedSize === "s" ? "small" : "normal"}
          color="inherit"
        />
      ) : iconEnd !== true ? (
        iconEnd
      ) : null;
    const resolvedLabel = children ?? label;
    const showLabel = resolvedLabel !== undefined;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        event.preventDefault();

        return;
      }

      onClick?.(event);
    };

    const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> &
      RefAttributes<HTMLButtonElement> = {
      className: cn(
        ButtonVariants({
          variant,
          size: resolvedSize,
          invertedTone,
        }),
        fullWidth && "w-full",
        hidden && "hidden",
        !showLabel && "min-w-[0px] p-2",
        className,
      ),
      disabled: isDisabled,
      onClick: handleClick,
      ...props,
      ref: forwardedRef as Ref<HTMLButtonElement> | undefined,
    };

    const buttonChildren = (
      <>
        {resolvedIconStart}
        {showLabel && <span className="flex-grow">{resolvedLabel}</span>}{" "}
        {resolvedIconEnd}
        {isLoading && <AiOutlineLoading3Quarters className="animate-spin" />}
      </>
    );

    if (resolvedTooltip !== undefined) {
      return (
        <Tooltip tooltip={resolvedTooltip}>
          <span className={cn("inline-flex", fullWidth && "w-full")}>
            <button {...buttonProps}>{buttonChildren}</button>
          </span>
        </Tooltip>
      );
    }

    return <button {...buttonProps}>{buttonChildren}</button>;
  },
);

export default Button;
